import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-pill border border-transparent bg-clip-padding [font-size:var(--text-body-sm)] [line-height:var(--text-body-sm--line-height)] font-medium whitespace-nowrap transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-standard select-none active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-40 aria-disabled:pointer-events-none aria-disabled:opacity-40 aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-[color:var(--z-on-brand)] shadow-brand-sm hover:bg-primary-hover active:bg-primary-active',
        outline:
          'border-border-strong bg-transparent text-foreground hover:bg-surface-hover active:bg-surface-active aria-expanded:bg-surface-active',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-surface-active aria-expanded:bg-surface-active',
        ghost:
          'text-foreground hover:bg-surface-hover active:bg-surface-active aria-expanded:bg-surface-active',
        destructive:
          'bg-danger-subtle text-destructive hover:bg-destructive/20 focus-visible:outline-destructive',
        link: 'text-brand underline-offset-4 hover:text-[var(--z-brand-hover)] hover:underline'
      },
      size: {
        default: 'h-9 gap-1.5 px-4',
        xs: "h-7 gap-1 px-3 [font-size:var(--text-caption)] [line-height:var(--text-caption--line-height)] [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1 px-3.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: 'h-11 gap-2 px-5 [font-size:var(--text-body)] [line-height:var(--text-body--line-height)]',
        icon: 'size-11',
        'icon-xs': "size-11 [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': "size-11 [&_svg:not([class*='size-'])]:size-3.5",
        'icon-lg': "size-11 [&_svg:not([class*='size-'])]:size-5"
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
